/**
 * 🎨 StatusBadge - Status Pill Badge
 * 
 * Varianten für verschiedene Status:
 * - success: Aktiv, Bestätigt, Ausgezahlt (Grün)
 * - warning: Ausstehend, In Bearbeitung (Orange)
 * - error: Abgelehnt, Fehler (Rot)
 * - info: Information, Hinweis (Blau)
 * - neutral: Default, Inaktiv (Grau)
 */

import React from 'react';
import { cn } from '@/lib/utils';
import { CheckCircle2, Clock, XCircle, AlertCircle, Info, type LucideIcon } from 'lucide-react';

// ============================================
// TYPES
// ============================================

export type BadgeVariant = 'success' | 'warning' | 'error' | 'info' | 'neutral';
export type BadgeSize = 'sm' | 'md' | 'lg';

export interface StatusBadgeProps {
  /** Badge Variante */
  variant: BadgeVariant;
  /** Angezeigter Text */
  label: string;
  /** Größe */
  size?: BadgeSize;
  /** Optional: Icon vor dem Text */
  icon?: LucideIcon;
  /** Dot Indicator statt Icon */
  showDot?: boolean;
  /** Zusätzliche CSS Klassen */
  className?: string;
}

// ============================================
// CONSTANTS
// ============================================

const VARIANT_STYLES: Record<BadgeVariant, { 
  bg: string; 
  text: string; 
  dot: string;
  defaultIcon: LucideIcon;
}> = {
  success: {
    bg: 'bg-green-50',
    text: 'text-green-700',
    dot: 'bg-green-500',
    defaultIcon: CheckCircle2,
  },
  warning: {
    bg: 'bg-orange-50',
    text: 'text-orange-700',
    dot: 'bg-orange-500',
    defaultIcon: Clock,
  },
  error: {
    bg: 'bg-red-50',
    text: 'text-red-700',
    dot: 'bg-red-500',
    defaultIcon: XCircle,
  },
  info: {
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    dot: 'bg-blue-500',
    defaultIcon: AlertCircle,
  },
  neutral: {
    bg: 'bg-gray-100',
    text: 'text-gray-700',
    dot: 'bg-gray-500',
    defaultIcon: Info,
  },
};

const SIZE_STYLES: Record<BadgeSize, {
  padding: string;
  text: string;
  iconSize: string;
  dotSize: string;
  gap: string;
}> = {
  sm: {
    padding: 'px-2 py-0.5',
    text: 'text-xs',
    iconSize: 'w-3 h-3',
    dotSize: 'w-1.5 h-1.5',
    gap: 'gap-1',
  },
  md: {
    padding: 'px-2.5 py-1',
    text: 'text-xs',
    iconSize: 'w-3.5 h-3.5',
    dotSize: 'w-2 h-2',
    gap: 'gap-1.5',
  },
  lg: {
    padding: 'px-3 py-1.5',
    text: 'text-sm',
    iconSize: 'w-4 h-4',
    dotSize: 'w-2 h-2',
    gap: 'gap-2',
  },
};

// ============================================
// MAIN COMPONENT
// ============================================

export const StatusBadge: React.FC<StatusBadgeProps> = ({
  variant,
  label,
  size = 'md',
  icon,
  showDot = false,
  className,
}) => {
  const variantStyle = VARIANT_STYLES[variant];
  const sizeStyle = SIZE_STYLES[size];
  
  const Icon = icon || variantStyle.defaultIcon;

  return (
    <span
      className={cn(
        // Base styles
        'inline-flex items-center rounded-full font-medium',
        // Size styles
        sizeStyle.padding,
        sizeStyle.text,
        sizeStyle.gap,
        // Variant styles
        variantStyle.bg,
        variantStyle.text,
        className
      )}
    >
      {/* Dot Indicator */}
      {showDot && (
        <span 
          className={cn(
            'rounded-full flex-shrink-0',
            sizeStyle.dotSize,
            variantStyle.dot
          )} 
        />
      )}
      
      {/* Icon (only if not showing dot) */}
      {!showDot && (
        <Icon className={cn('flex-shrink-0', sizeStyle.iconSize)} />
      )}
      
      {/* Label */}
      <span>{label}</span>
    </span>
  );
};

// ============================================
// PRESET BADGES
// ============================================

export const ActiveBadge: React.FC<{ size?: BadgeSize; className?: string }> = (props) => (
  <StatusBadge variant="success" label="Aktiv" showDot {...props} />
);

export const PendingBadge: React.FC<{ size?: BadgeSize; className?: string }> = (props) => (
  <StatusBadge variant="warning" label="Ausstehend" {...props} />
);

export const RejectedBadge: React.FC<{ size?: BadgeSize; className?: string }> = (props) => (
  <StatusBadge variant="error" label="Abgelehnt" {...props} />
);

export const PaidBadge: React.FC<{ size?: BadgeSize; className?: string }> = (props) => (
  <StatusBadge variant="success" label="Ausgezahlt" {...props} />
);

export const ProcessingBadge: React.FC<{ size?: BadgeSize; className?: string }> = (props) => (
  <StatusBadge variant="info" label="In Bearbeitung" {...props} />
);

export default StatusBadge;
